import React, { useEffect, useRef, useCallback } from 'react';
import { SettingsPanel } from './SettingsPanel';
import { SimulationHUD } from './SimulationHUD';
import { SimulationHeader } from './SimulationHeader';
import { SimulationFooter } from './SimulationFooter';
import { ControlIsland } from './ControlIsland';
import { STORAGE_KEY } from './constants';
import { SimulationRenderer } from './render/SimulationRenderer';
import { safeSetItem } from './lib/storage';

// Hooks
import { useSimulationState } from './hooks/useSimulationState';
import { useAudioTriggers } from './hooks/useAudioTriggers';
import { useFileHandlers } from './hooks/useFileHandlers';
import { useSimulationInit } from './hooks/useSimulationInit';
import { useSimulationLoop } from './hooks/useSimulationLoop';
import { usePhysicsWorker } from './hooks/usePhysicsWorker';
import { useWebGL } from './hooks/useWebGL';
import { useKeyboardControls } from './hooks/useKeyboardControls';
import { useCloudSync } from './hooks/useCloudSync';
import { useAudioResume } from './hooks/useAudioResume';
import { AUDIO } from './services/AudioService';

// Register all animations
import './logic/animations/collisions';
import './logic/animations/blasts';

const App: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const webglCanvasRef = useRef<HTMLCanvasElement>(null);
  const webglRendererRef = useRef<any>(null);

  const state = useSimulationState();
  const {
    config, setConfig,
    ballCount, setBallCount,
    bounceCount, setBounceCount,
    bounceCountRef,
    isPlaying, setIsPlaying,
    showUI, setShowUI,
    elapsedTime, setElapsedTime,
    saveStatus, setSaveStatus,
    cloudSaves, setCloudSaves,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    requestRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef
  } = state;

  // Expose setConfig to window for components that use it via (window as any).setConfig
  useEffect(() => {
    (window as any).setConfig = setConfig;
  }, [setConfig]);

  const hyperSpeedStateRef = useRef<{ active: boolean, startTime: number }>({ active: false, startTime: 0 });
  const zoomLevelRef = useRef<number>(1.0);

  const configRef = useRef(config);
  useEffect(() => { configRef.current = config; }, [config]);

  // Audio & Cloud
  useAudioResume();
  useCloudSync(setConfig, setCloudSaves);

  // Audio Triggers
  const { playCollisionSound, triggerBlast } = useAudioTriggers(
    configRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    blastEventsRef,
    morphStateRef,
    containerRadiusRef,
    shakeRef,
    isRotationStoppedRef,
    setBounceCount,
    counterPopRef
  );

  // File Handlers
  const {
    handleFileUpload,
    handleBallSoundUpload,
    isAnalyzing,
    analysisProgress,
    isUploadingBallSound
  } = useFileHandlers(config, setConfig);

  // Physics & WebGL
  const onCollision = useCallback((payload: any) => {
    const { ballIndex, type, pos, intensity } = payload;
    const now = Date.now();
    const effectiveCfg = configRef.current;
    const b = ballsRef.current[ballIndex];

    if (!b) return;

    if (type === 'WALL') {
        // Handle Spawner Logic
        if (effectiveCfg.spawnOnWallCollision && effectiveCfg.mode === 'SPAWNER' && ballsRef.current.length < effectiveCfg.maxBalls && now - (b.lastSpawnTime || 0) > 150) {
            const newBall = ballPoolRef.current.get(Math.random().toString(), { ...pos }, { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 }, effectiveCfg.ballRadius, b.color, effectiveCfg.ballMass, effectiveCfg.ballStyle);
            ballsRef.current.push(newBall);
            if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: [newBall] } });
            b.lastSpawnTime = now;
            setBallCount(ballsRef.current.length);
        }

        // Visual Effects
        collisionEventsRef.current.push({ 
            id: Math.random().toString(), 
            pos: { ...pos }, 
            startTime: now, 
            radius: b.radius * 2.5, 
            color: b.color, 
            effectType: effectiveCfg.collisionEffect, 
            duration: effectiveCfg.collisionEffectDuration || 800 
        });

        // Audio
        if (intensity > 0.2) {
            AUDIO.playCollisionSound(intensity, effectiveCfg);
            shakeRef.current = Math.max(shakeRef.current, 2 * effectiveCfg.shakeIntensityMultiplier);
        }
        
        setBounceCount(prev => prev + 1);
    }
  }, [setBallCount, setBounceCount]);

  const { resetWorker, sendBallsToWorker } = usePhysicsWorker(config, ballsRef, workerRef, onCollision);

  // Initialization
  const { initSimulation, getBallPreviews } = useSimulationInit(
    configRef,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    containerRadiusRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    ballPoolRef,
    trailCirclePoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    resetWorker,
    sendBallsToWorker,
    setBallCount,
    setBounceCount,
    setElapsedTime,
    setConfig,
    lastTimeRef
  );

  // Renderer
  const draw = useCallback(() => {
    if (!canvasRef.current) return;
    
    const previews = !isPlaying ? getBallPreviews() : undefined;

    SimulationRenderer.draw(
      canvasRef.current,
      bgCanvasRef.current,
      webglRendererRef.current,
      ballsRef.current,
      trailCirclesRef.current,
      collisionEventsRef.current,
      blastEventsRef.current,
      persistentSpectrumBoostRef.current,
      escapeRingsRef.current,
      initialEscapeRingCountRef.current,
      configRef.current,
      hueRef.current,
      shakeRef.current,
      counterPopRef.current,
      timeRemainingRef.current,
      morphStateRef.current,
      containerRadiusRef.current,
      rhythmicBreakPiecesRef.current,
      zoomLevelRef.current,
      bounceCountRef.current,
      previews,
      rhythmicSimulationsStateRef.current,
      rhythmicEscapeShieldActiveRef.current,
      rhythmicEscapeShieldAngleRef.current
    );
  }, [isPlaying, getBallPreviews]);

  // Loop
  const { update } = useSimulationLoop(
    configRef,
    isPlaying,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicResetTimeoutRef,
    rhythmicIndexRef,
    rhythmicSimulationsStateRef,
    hyperSpeedStateRef,
    zoomLevelRef,
    requestRef,
    simTimeRef,
    setElapsedTime,
    setBounceCount,
    setBallCount,
    setConfig,
    setIsPlaying,
    triggerBlast,
    playCollisionSound,
    initSimulation,
    draw,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef
  );

  // Keyboard Controls
  const onThrow = useCallback(() => {
    const heroBall = ballsRef.current.find(b => b.id === 'hero-ball');
    if (heroBall) {
        const angleRad = (configRef.current.spaceSpiralThrowAngle || 0) * Math.PI / 180;
        const power = configRef.current.spaceSpiralThrowPower || 20;
        heroBall.vel.x = Math.cos(angleRad) * power;
        heroBall.vel.y = Math.sin(angleRad) * power;
        // Temporarily increase mass to push through boxes
        const originalMass = heroBall.mass;
        heroBall.mass = 10000;
        setTimeout(() => {
            heroBall.mass = originalMass;
        }, 1000);
    }
  }, []);

  useWebGL(webglCanvasRef, webglRendererRef);
  useKeyboardControls(config, isPlaying, setIsPlaying, initSimulation, isRotationStoppedRef, onThrow);

  useEffect(() => {
    if (isPlaying) {
      AUDIO.resume();
      if (configRef.current.mode === 'SPACE_SPIRAL' && configRef.current.spaceSpiralSoundSpawn) {
        AUDIO.playSoundEffect(configRef.current.spaceSpiralSoundSpawn, configRef.current);
      }
    }
  }, [isPlaying]);

  const prevSelectedMelodyRef = useRef(config.selectedMelody);
  const prevModeRef = useRef(config.mode);
  const prevPlaylistEnabledRef = useRef(config.playlistEnabled);

  useEffect(() => { 
    const playlistChanged = prevPlaylistEnabledRef.current !== config.playlistEnabled;
    const melodyChanged = prevSelectedMelodyRef.current !== config.selectedMelody;
    const modeChanged = prevModeRef.current !== config.mode;
    
    prevPlaylistEnabledRef.current = config.playlistEnabled;
    prevSelectedMelodyRef.current = config.selectedMelody;
    prevModeRef.current = config.mode;

    // Skip reset if only melody or playlist toggle changed and continue simulation is enabled
    const continueSim = config.mode === 'RHYTHMIC' && config.rhythmicPlaylistContinueSimulation;
    if ((melodyChanged || playlistChanged) && !modeChanged && continueSim) {
        return;
    }

    initSimulation(true); 
  }, [
    config.mode, 
    config.initialBalls, 
    config.escapeRingCount, 
    config.containerRadius,
    config.playlistEnabled,
    config.spaceSpiralBoxCount,
    config.spaceSpiralBoxSpacing,
    config.spaceSpiralBoxWidth,
    config.spaceSpiralBoxHeight,
    config.spaceSpiralScale,
    config.spaceSpiralMinBoxScale,
    config.spaceSpiralMaxBoxScale,
    config.spaceSpiralRotation,
    config.spaceSpiralBoxRotation,
    config.spaceSpiralBoxShape,
    config.spaceSpiralRepeats,
    config.spaceSpiralBallOffsetX,
    config.spaceSpiralBallOffsetY,
    config.selectedMelody,
    config.customMelodies,
    config.rhythmicPlaylistContinueSimulation,
    initSimulation
  ]);

  useEffect(() => {
    const resize = () => { 
        const dpr = window.devicePixelRatio || 1;
        const w = window.innerWidth;
        const h = window.innerHeight;

        if (canvasRef.current) { 
            canvasRef.current.width = w * dpr; 
            canvasRef.current.height = h * dpr; 
            canvasRef.current.style.width = `${w}px`;
            canvasRef.current.style.height = `${h}px`;
        }
        if (bgCanvasRef.current) {
            bgCanvasRef.current.width = w * dpr;
            bgCanvasRef.current.height = h * dpr;
            bgCanvasRef.current.style.width = `${w}px`;
            bgCanvasRef.current.style.height = `${h}px`;
        }
        if (webglCanvasRef.current) {
            webglCanvasRef.current.width = w * dpr;
            webglCanvasRef.current.height = h * dpr;
            webglCanvasRef.current.style.width = `${w}px`;
            webglCanvasRef.current.style.height = `${h}px`;
        }
        if (webglRendererRef.current) {
            webglRendererRef.current.resize(w * dpr, h * dpr);
        }
        if (workerRef.current) {
            workerRef.current.postMessage({ 
                type: 'UPDATE_DIMENSIONS', 
                payload: { width: w, height: h } 
            });
        }
    };
    window.addEventListener('resize', resize); resize(); 
    requestRef.current = requestAnimationFrame(update);
    return () => { 
        window.removeEventListener('resize', resize); 
        cancelAnimationFrame(requestRef.current); 
    };
  }, [update, requestRef]);

  useEffect(() => {
    if (config.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [config.darkMode]);

  return (
    <div className={`relative w-full h-screen font-sans overflow-hidden transition-colors duration-500 ${config.darkMode ? 'bg-neutral-950 text-white' : 'bg-[#fafaf5] text-neutral-800'}`} style={{ backgroundColor: config.darkMode ? (config.rgbLighting ? '#050505' : (config.outerColor === '#f5f5dc' ? '#0a0a0a' : config.outerColor)) : config.outerColor }}>
      <canvas ref={bgCanvasRef} className="absolute inset-0 z-0 pointer-events-none" />
      <canvas ref={webglCanvasRef} className="absolute inset-0 pointer-events-none z-10" />
      <canvas ref={canvasRef} className="absolute inset-0 cursor-pointer z-20" onClick={() => setIsPlaying(!isPlaying)} />
      <SimulationHUD 
        elapsedTime={elapsedTime} 
        timeScale={timeScaleRef.current} 
        ballCount={ballCount}
        bounceCount={bounceCount}
        ringsLeft={escapeRingsRef.current.length}
        timeRemaining={timeRemainingRef.current}
        currentRound={currentRoundRef.current}
        containerRadius={containerRadiusRef.current}
        ballRadius={config.ballRadius}
        gravity={config.gravity}
        restitution={config.restitution}
        config={config} 
      />
      {!config.renderOrder.includes('HUD') && (
        <>
          <SimulationHeader config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} ballCount={ballCount} ringsLeft={escapeRingsRef.current.length} bounceCount={bounceCount} isEscape={config.mode === 'ESCAPE'} />
          <SimulationFooter config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} />
        </>
      )}
      {config.showControlIsland && (
        <ControlIsland 
          isPlaying={isPlaying} 
          setIsPlaying={setIsPlaying} 
          onReset={() => initSimulation(true)} 
          config={config} 
        />
      )}
      <SettingsPanel 
        config={config} 
        setConfig={setConfig} 
        showUI={showUI} 
        isPlaying={isPlaying} 
        setIsPlaying={setIsPlaying} 
        onReset={() => initSimulation(true)} 
        onSave={() => { safeSetItem(STORAGE_KEY, JSON.stringify(config)); setSaveStatus('saved'); setTimeout(() => setSaveStatus('idle'), 2000); }} 
        saveStatus={saveStatus} 
        onFileUpload={handleFileUpload} 
        isAnalyzing={isAnalyzing} 
        analysisProgress={analysisProgress} 
        onBallSoundUpload={handleBallSoundUpload} 
        isUploadingBallSound={isUploadingBallSound}
        cloudSaves={cloudSaves}
        setCloudSaves={setCloudSaves}
      />
      <div className="absolute bottom-6 right-6 z-30 flex flex-col gap-3">
         <button onClick={() => setShowUI(!showUI)} className={`w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all pointer-events-auto ${config.darkMode ? 'bg-white text-black' : 'bg-black text-white'}`}>
            <i className={`fa-solid ${showUI ? 'fa-times' : 'fa-gear'}`}></i>
         </button>
      </div>
    </div>
  );
};

export default App;
